import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, sectionHeadingSx, sectionGapSx,
} from '../portalStyles';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import { getStackSetsConfig } from '../../../services/stacksetsApi';
import StackSetsSetup from './StackSetsSetup';
import StackSetsList from './StackSetsList';
import RoleStatusPanel from './RoleStatusPanel';
import OperationProgress from '../securityhub/OperationProgress';
import { POLL_INTERVAL_MS } from '../securityhub/pollInterval';

const PAGE_TITLE = 'CloudFormation StackSets';
const PAGE_SUBTITLE = "Deploy templates to accounts across your organization's OUs";

// StackSetsEntry decides what to render at /stacksets:
//   no config / disabled / failed  → setup wizard
//   in_progress                    → transitioning view (polls until done)
//   enabled / partial_failure      → StackSets list
export default function StackSetsEntry() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getStackSetsConfig();
      setConfig(res?.data || null);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll during any in_progress transition (enable or disable) so the view
  // advances automatically once the orchestrator goroutine finishes. Keyed on
  // config.status (a primitive), so the cleanup only fires on a real status
  // change or unmount — not on every poll tick.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    pollRef.current = setInterval(() => refresh({ showSpinner: false }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [config?.status, refresh]);

  // Loading and error keep the page header mounted so the layout does not jump
  // once the config lands — only the card body below it changes.
  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading StackSets…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => refresh()} />
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'in_progress') {
    const hint = (config.errorMessage || '').toLowerCase();
    const disabling = hint.includes('disabl');
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader
            icon={<LayersIcon sx={{ fontSize: 24 }} />}
            title={PAGE_TITLE}
            subtitle={disabling ? 'Disabling the service…' : 'Setting up the service…'}
          />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>
              {disabling ? 'Disabling StackSets…' : 'Setting up StackSets…'}
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              {disabling
                ? 'Removing execution roles from member accounts and cleaning up the admin account.'
                : 'Creating the administration role and template bucket, then provisioning an execution role in every onboarded account. This can take a few minutes.'}
            </Typography>
            <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} />
          </Paper>
        </motion.div>
        {/* Per-account grid (docs: "the progress screen shows a per-account
            grid"). Retry is hidden here — the backend rejects retries until
            the fan-out finishes; failed rows become retryable on the list
            page once the config settles. */}
        <motion.div variants={sectionVariants}>
          <Box sx={sectionGapSx}>
            <RoleStatusPanel adminAccountId={config.adminAccountId} showRetry={false} />
          </Box>
        </motion.div>
      </motion.div>
    );
  }

  if (!config || config.status === 'disabled' || config.status === 'failed') {
    return (
      <StackSetsSetup
        onComplete={refresh}
        priorFailure={config?.status === 'failed' ? config.errorMessage : null}
      />
    );
  }

  return <StackSetsList config={config} onConfigChange={refresh} />;
}
